import { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { ArrowLeft, Download, Printer, Receipt, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import SiteDisclaimer from '@/components/SiteDisclaimer';
import { useAuth } from '@/contexts/AuthContext';
import { showReferralToast } from '@/utils/referralToast';
import { downloadCsv } from '@/lib/csvExport';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Tax-Harvest Report — year-end ledger of every sell tagged with
// tax_harvest=true. Pulls from the /portfolio/tax-harvest-report
// endpoint, supports year-pick (defaults to current year), prints as a
// clean single-color sheet via window.print(), and exports as CSV for
// 1099-B reconciliation. Designed so a user can hand the printout (or
// the CSV) to a tax pro and have everything they need on one page.
//
// NOT TAX ADVICE — disclaimer at the top + bottom + on every printed
// copy. Wash-sale logic is OUT OF SCOPE here (the Tax Lot Optimizer
// surfaces wash risk before the harvest happens; this report is a
// post-harvest record, not a what-if).

const fmtMoney = (v, signed = false) => {
  if (v == null || !Number.isFinite(Number(v))) return '—';
  const n = Number(v);
  const sign = signed ? (n >= 0 ? '+' : '−') : '';
  return `${sign}$${Math.abs(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

export default function TaxHarvestReport() {
  const { user } = useAuth();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const { data } = await axios.get(`${API}/portfolio/tax-harvest-report`, {
          params: { year },
          withCredentials: true,
        });
        if (!cancelled) setData(data);
      } catch (err) {
        if (!cancelled) setError(err?.response?.data?.detail || err?.message || 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [year]);

  const summary = data?.summary || {};
  const sales = data?.sales || [];
  const byTicker = data?.by_ticker || [];
  // Year picker spans the last 5 years through the current year so
  // users can revisit historical filings (data only exists going
  // forward from when the harvest tag launched, but old years return
  // an empty list cleanly).
  const yearOptions = [];
  for (let y = now.getFullYear(); y >= now.getFullYear() - 5; y--) yearOptions.push(y);

  const handlePrint = () => {
    window.print();
    // Same rationale as Year-End Snapshot — the print dialog is the
    // emotional peak for a tax-prep user. Helper debounces under the
    // hood so the toast lands after the dialog closes.
    showReferralToast({ kind: 'harvest', user });
  };
  const handleCsvExport = () => {
    const headerLine = ['Ticker', 'Sale Date', 'Shares', 'Sale Price', 'Cost Basis/Share', 'Proceeds', 'Realised Gain/Loss'];
    const dataLines = sales.map(s => [
      s.ticker, s.date, s.shares, s.sale_price, s.harvest_cost_basis, s.proceeds, s.realised_loss,
    ]);
    const totalLine = ['TOTAL', '', '', '', '', summary.realised_loss_total || 0, summary.total_realised || 0];
    downloadCsv(`dividrip-harvest-report-${year}.csv`, [headerLine, ...dataLines, totalLine]);
    showReferralToast({ kind: 'harvest', user });
  };

  return (
    <div className="min-h-screen min-h-[100svh] bg-background text-foreground">
      {/* Print-only stylesheet: hide chrome (global site header,
          toast portal, year picker, action buttons), force white
          background + black text, drop shadows. Tested in Chrome /
          Safari / Firefox. Also applies when the user hits
          Print → Save as PDF in the browser dialog (this page has
          no separate html2pdf export). Round 92o++++++++h++ (Feb
          2026) — extended to match the light-theme + hide-chrome
          behaviour we already ship for the Year-End Snapshot and
          Tax Account Audit reports. */}
      <style>{`
        @media print {
          /* Hide the global AppHeader (logo + nav pills) and the
             sonner toast portal — the printed artifact must be the
             report content only. */
          header,
          [data-sonner-toaster],
          [data-sonner-toaster] * {
            display: none !important;
          }
          .no-print { display: none !important; }
          body { background: white !important; color: black !important; }
          .print-clean { background: white !important; color: black !important; box-shadow: none !important; border-color: #ccc !important; }
          a { color: black !important; text-decoration: none !important; }
          /* Force LIGHT theme regardless of the app's active theme
             — dark-mode printouts were washed out and hard to read
             at the top. Individual semantic tints (loss red, gain
             green, brand cyan) are preserved by the per-utility
             overrides below. Scoped to "body *" under @media print
             because this file has no #th-printable wrapper (unlike
             the Year-End Snapshot page). */
          body *:not([data-sonner-toaster]):not([data-sonner-toaster] *) {
            color: black !important;
            background-color: transparent !important;
            border-color: #ccc !important;
          }
          .print-clean,
          [data-pdf-keep] {
            background-color: #ffffff !important;
          }
          .text-emerald-600,
          .text-emerald-500,
          .text-emerald-700 { color: #059669 !important; }
          .text-red-600,
          .text-red-500,
          .text-red-700,
          .text-rose-600,
          .text-rose-500,
          .text-rose-700 { color: #dc2626 !important; }
          .text-cyan-600,
          .text-cyan-500,
          .text-cyan-700 { color: #0891b2 !important; }
          .text-amber-600,
          .text-amber-500,
          .text-amber-700 { color: #d97706 !important; }
          .text-violet-600,
          .text-violet-500,
          .text-violet-700 { color: #7c3aed !important; }
          .text-muted-foreground { color: #64748b !important; }
        }
      `}</style>

      <div className="max-w-[1100px] mx-auto px-4 sm:px-6 pt-6 pb-0">
        {/* Header — hidden in print */}
        <div className="flex items-center justify-between mb-6 gap-2 no-print">
          <div className="flex items-center gap-3">
            <Link to="/tax-lots" data-testid="harvest-report-back-link">
              <Button variant="ghost" size="sm"><ArrowLeft className="w-4 h-4 mr-1" /> Tax Lot Optimizer</Button>
            </Link>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2">
                <Receipt className="w-7 h-7 text-cyan-600 dark:text-cyan-400" />
                Tax-Harvest Report
              </h1>
              <p className="text-xs text-muted-foreground mt-0.5">
                Year-end ledger of every sale you tagged as a tax-loss harvest. Print or export for your tax filing.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              className="h-9 px-2 rounded-lg border bg-card text-sm font-mono font-bold"
              data-testid="harvest-report-year-select"
            >
              {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
            <button
              type="button"
              onClick={handleCsvExport}
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border bg-card hover:bg-muted text-sm font-semibold transition-colors disabled:opacity-50"
              disabled={!sales.length}
              data-testid="harvest-report-csv-btn"
              title="Download as CSV — opens in Excel / Google Sheets"
            >
              <Download className="w-3.5 h-3.5" />
              CSV
            </button>
            <button
              type="button"
              onClick={handlePrint}
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-cyan-600 hover:bg-cyan-700 text-white text-sm font-semibold transition-colors disabled:opacity-50"
              disabled={!sales.length}
              data-testid="harvest-report-print-btn"
              title="Print or save as PDF — clean single-color layout designed for handing to a tax pro"
            >
              <Printer className="w-3.5 h-3.5" />
              Print
            </button>
          </div>
        </div>

        {/* Print-visible header — only renders on paper. Includes a
            "DiviDrip · twylightcrow.com" line so the printout has the
            generator stamped on it for the tax pro. */}
        <div className="hidden print:block mb-4">
          <h1 className="text-2xl font-bold">Tax-Harvest Report — {year}</h1>
          <p className="text-xs text-gray-600">
            Generated by DiviDrip (twylightcrow.com) · {new Date().toLocaleDateString()}
          </p>
        </div>

        {/* Disclaimer — required given page name; visible in print too. */}
        <div className="rounded-lg border border-amber-300/60 bg-amber-50/50 dark:border-amber-700/50 dark:bg-amber-950/20 p-3 mb-5 text-[11px] text-amber-900 dark:text-amber-200 print-clean" data-testid="harvest-report-disclaimer">
          <strong>Educational record, not tax advice.</strong> This report shows transactions you tagged as tax-loss harvests. The numbers come from the cost basis you entered (or the CSV importer captured) and the sale price you confirmed. Wash-sale rules, qualified-dividend treatment, state taxes, and adjustments for return-of-capital distributions can change the numbers a tax pro records on your 1099-B / Schedule D. Always reconcile with the official 1099-B from your broker.
        </div>

        {loading && (
          <div className="rounded-lg border border-border bg-muted/30 p-8 text-center text-sm text-muted-foreground" data-testid="harvest-report-loading">
            Loading {year} harvest data…
          </div>
        )}

        {error && (
          <div className="rounded-lg border border-rose-300/60 bg-rose-50/50 dark:bg-rose-950/20 p-4 text-sm text-rose-700 dark:text-rose-300 flex items-start gap-2" data-testid="harvest-report-error">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            <div>
              <strong>Couldn&rsquo;t load the report.</strong>
              <div className="mt-1">{error}</div>
            </div>
          </div>
        )}

        {!loading && !error && sales.length === 0 && (
          <div className="rounded-lg border border-border bg-muted/30 p-8 text-center text-sm text-muted-foreground" data-testid="harvest-report-empty">
            <p className="mb-2">No tax-loss harvests recorded for {year}.</p>
            <p className="text-xs">
              When selling a loss lot, tick the <strong>&ldquo;Tag as tax-loss harvest&rdquo;</strong> checkbox on the Sell form (or use the rose <strong>&ldquo;Harvest this lot&rdquo;</strong> shortcut on the Stock Modal&rsquo;s My Portfolio tab). Tagged sales appear here automatically.
            </p>
          </div>
        )}

        {!loading && !error && sales.length > 0 && (
          <>
            {/* Summary tiles */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6 print:grid-cols-4">
              <Tile label={`Harvests in ${year}`} value={summary.harvest_count} mono testId="hr-tile-count" />
              <Tile label="Realised Losses" value={fmtMoney(summary.realised_loss_total)} accent="rose" testId="hr-tile-losses" />
              <Tile label="Realised Gains" value={fmtMoney(summary.realised_gain_total)} accent={summary.realised_gain_total > 0 ? 'emerald' : 'neutral'} testId="hr-tile-gains" />
              <Tile label="Net Realised G/L" value={fmtMoney(summary.total_realised, true)} accent={summary.total_realised >= 0 ? 'emerald' : 'rose'} testId="hr-tile-net" />
            </div>

            {/* By-ticker rollup */}
            {byTicker.length > 0 && (
              <div className="rounded-xl border bg-card mb-5 print-clean" data-testid="harvest-report-by-ticker">
                <h3 className="text-sm font-bold px-4 py-2 border-b bg-muted/40 print-clean">By Ticker</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/30 print-clean">
                      <tr>
                        <th className="px-3 py-2 text-left text-[11px] uppercase tracking-wider font-bold text-muted-foreground">Ticker</th>
                        <th className="px-3 py-2 text-right text-[11px] uppercase tracking-wider font-bold text-muted-foreground">Harvests</th>
                        <th className="px-3 py-2 text-right text-[11px] uppercase tracking-wider font-bold text-muted-foreground">Total Shares</th>
                        <th className="px-3 py-2 text-right text-[11px] uppercase tracking-wider font-bold text-muted-foreground">Total Proceeds</th>
                        <th className="px-3 py-2 text-right text-[11px] uppercase tracking-wider font-bold text-muted-foreground">Realised G/L</th>
                      </tr>
                    </thead>
                    <tbody>
                      {byTicker.map(t => (
                        <tr key={t.ticker} className="border-t border-border odd:bg-muted/20" data-testid={`hr-ticker-row-${t.ticker}`}>
                          <td className="px-3 py-2 font-mono font-bold">{t.ticker}</td>
                          <td className="px-3 py-2 text-right font-mono text-xs">{t.harvest_count}</td>
                          <td className="px-3 py-2 text-right font-mono text-xs">{Number(t.total_shares || 0).toFixed(4)}</td>
                          <td className="px-3 py-2 text-right font-mono text-xs">{fmtMoney(t.total_proceeds)}</td>
                          <td className={`px-3 py-2 text-right font-mono text-xs font-bold ${t.total_realised < 0 ? 'text-rose-600 dark:text-rose-400' : 'text-emerald-600 dark:text-emerald-400'}`}>
                            {fmtMoney(t.total_realised, true)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Detail ledger */}
            <div className="rounded-xl border bg-card print-clean" data-testid="harvest-report-detail">
              <h3 className="text-sm font-bold px-4 py-2 border-b bg-muted/40 print-clean">Detail (chronological)</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/30 print-clean">
                    <tr>
                      <th className="px-3 py-2 text-left text-[11px] uppercase tracking-wider font-bold text-muted-foreground">Date</th>
                      <th className="px-3 py-2 text-left text-[11px] uppercase tracking-wider font-bold text-muted-foreground">Ticker</th>
                      <th className="px-3 py-2 text-right text-[11px] uppercase tracking-wider font-bold text-muted-foreground">Shares</th>
                      <th className="px-3 py-2 text-right text-[11px] uppercase tracking-wider font-bold text-muted-foreground">Sale $/Sh</th>
                      <th className="px-3 py-2 text-right text-[11px] uppercase tracking-wider font-bold text-muted-foreground">Cost $/Sh</th>
                      <th className="px-3 py-2 text-right text-[11px] uppercase tracking-wider font-bold text-muted-foreground">Proceeds</th>
                      <th className="px-3 py-2 text-right text-[11px] uppercase tracking-wider font-bold text-muted-foreground">Realised G/L</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sales.map(s => (
                      <tr key={s.tx_id} className="border-t border-border odd:bg-muted/20" data-testid={`hr-detail-row-${s.tx_id}`}>
                        <td className="px-3 py-2 font-mono text-xs">{s.date}</td>
                        <td className="px-3 py-2 font-mono font-bold">{s.ticker}</td>
                        <td className="px-3 py-2 text-right font-mono text-xs">{Number(s.shares || 0).toFixed(4)}</td>
                        <td className="px-3 py-2 text-right font-mono text-xs">{fmtMoney(s.sale_price)}</td>
                        <td className="px-3 py-2 text-right font-mono text-xs">{fmtMoney(s.harvest_cost_basis)}</td>
                        <td className="px-3 py-2 text-right font-mono text-xs">{fmtMoney(s.proceeds)}</td>
                        <td className={`px-3 py-2 text-right font-mono text-xs font-bold ${Number(s.realised_loss) < 0 ? 'text-rose-600 dark:text-rose-400' : 'text-emerald-600 dark:text-emerald-400'}`}>
                          {fmtMoney(s.realised_loss, true)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-border bg-muted/40 font-bold print-clean">
                      <td className="px-3 py-2 text-xs uppercase tracking-wider" colSpan={5}>Total</td>
                      <td className="px-3 py-2 text-right font-mono text-xs">
                        {fmtMoney(sales.reduce((sum, s) => sum + Number(s.proceeds || 0), 0))}
                      </td>
                      <td className={`px-3 py-2 text-right font-mono text-xs ${summary.total_realised < 0 ? 'text-rose-600 dark:text-rose-400' : 'text-emerald-600 dark:text-emerald-400'}`}>
                        {fmtMoney(summary.total_realised, true)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </>
        )}
      </div>

      <div className="no-print">
        <SiteDisclaimer />
      </div>
    </div>
  );
}

function Tile({ label, value, accent = 'neutral', mono, testId }) {
  const accentCls =
    accent === 'rose' ? 'border-rose-300/60 bg-rose-50/40 dark:border-rose-700/50 dark:bg-rose-950/20'
    : accent === 'emerald' ? 'border-emerald-300/60 bg-emerald-50/40 dark:border-emerald-700/50 dark:bg-emerald-950/20'
    : 'border-border bg-muted/30';
  const valueCls =
    accent === 'rose' ? 'text-rose-700 dark:text-rose-300'
    : accent === 'emerald' ? 'text-emerald-700 dark:text-emerald-300'
    : 'text-foreground';
  return (
    <div className={`rounded-xl border-2 p-4 print-clean ${accentCls}`} data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">{label}</div>
      <div className={`text-2xl font-bold mt-1 ${mono ? 'font-mono' : 'font-mono'} ${valueCls}`}>
        {value}
      </div>
    </div>
  );
}
